import { Suspense } from "react";
import { Outlet } from "react-router";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { Topbar } from "@/components/layout/topbar";

/**
 * O estado do menu, lido do cookie que o `sidebar.tsx` já escreve.
 *
 * ── O cookie era write-only (28/08, slide 51) ──────────────────────────────
 * O componente do shadcn grava `sidebar_state` a cada alternância — e não havia
 * uma única leitura em todo o `src/`. É o ficheiro tal como vem para o Next.js,
 * onde é o `layout.tsx` do servidor que faz `cookies().get(…)` e passa
 * `defaultOpen`. Numa SPA ninguém faz esse papel, portanto o menu arrancava
 * sempre aberto e a escrita era código morto.
 *
 * ⚠ Fica AQUI e não no `sidebar.tsx`: aquele ficheiro é gerado pelo
 * `shadcn add` e uma alteração à mão perde-se na primeira regeneração.
 *
 * ⚠ E o `document.cookie` lê-se dentro da função, a cada montagem — não numa
 * constante de módulo. Uma constante seria avaliada uma vez por carregamento
 * do bundle, e passaria a servir um valor obsoleto se a árvore remontasse.
 */
function menuAberto(): boolean {
  if (typeof document === "undefined") return true;
  const valor = document.cookie
    .split("; ")
    .find((c) => c.startsWith("sidebar_state="))
    ?.split("=")[1];
  // Sem cookie, aberto — é o padrão do shadcn e o que faz sentido na primeira
  // visita, onde ninguém sabe ainda que o menu se pode fechar.
  return valor !== "false";
}

export function AppShell() {
  return (
    <SidebarProvider defaultOpen={menuAberto()}>
      <AppSidebar />
      {/* ⚠ `min-w-0` também AQUI (Sprint V): o `SidebarInset` é um item flex ao
          lado da sidebar e, sem isto, não encolhe abaixo do min-content do seu
          conteúdo — uma tabela larga (a Alocação de recursos) empurrava a página
          inteira para lá do ecrã. O `min-w-0` do `<main>` interno não chega se o
          pai não encolher primeiro. */}
      <SidebarInset className="min-w-0">
        <Topbar />
        {/* `min-w-0` é essencial: sem isto, uma grelha de 60 colunas força o
            flex a crescer e a página inteira ganha scroll horizontal em vez de
            o scroll ficar contido dentro da grelha. */}
        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Suspense fallback={<Skeleton className="h-96 w-full" />}>
            <Outlet />
          </Suspense>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
